import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PublicLayout } from "@/components/Navbar";
import { Trash2, Send } from "lucide-react";

export default function EnquiryList() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);

  useEffect(() => { setItems(JSON.parse(localStorage.getItem("mc_enquiry") || "[]")); }, []);

  const remove = (sku) => {
    const next = items.filter((i) => i.sku !== sku);
    setItems(next);
    localStorage.setItem("mc_enquiry", JSON.stringify(next));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/public/enquiry", { ...form, items });
      localStorage.removeItem("mc_enquiry");
      setItems([]);
      setSent(true);
      toast.success("Enquiry sent to our trade team");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-4xl px-4 py-14 sm:px-8">
        <h1 className="font-serif-display text-4xl font-bold text-slate-50">Your <span className="mc-gold-gradient">Enquiry List</span></h1>
        <p className="mt-3 text-sm text-[#94A3B8]">Send us the products you're interested in and our trade team will follow up with pricing.</p>

        {sent ? (
          <div className="mt-12 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center text-slate-100">
            Thank you — your enquiry has been received. <Link to="/catalog" className="text-[#D4AF37]">Back to catalogue</Link>
          </div>
        ) : items.length === 0 ? (
          <div className="mt-12 rounded-2xl border border-[#2A2E39] p-12 text-center text-[#64748B]">
            Your enquiry list is empty. <Link to="/catalog" className="text-[#D4AF37]">Browse products</Link> and add items.
          </div>
        ) : (
          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <div className="space-y-3">
              {items.map((it) => (
                <div key={it.sku} data-testid={`enquiry-item-${it.sku}`} className="flex items-center gap-4 rounded-xl border border-[#2A2E39] bg-[#1A1D24] p-3">
                  <img src={it.image_url} alt={it.name} className="h-14 w-14 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="font-medium text-slate-100">{it.name}</div>
                    <div className="text-xs text-[#94A3B8] font-mono-data">{it.sku} • {it.brand}</div>
                  </div>
                  <button onClick={() => remove(it.sku)} data-testid={`enquiry-remove-${it.sku}`} className="rounded-lg p-2 text-[#94A3B8] hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <form onSubmit={submit} className="space-y-4 rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
              {["name", "email", "company", "phone"].map((k) => (
                <input key={k} required={k === "name" || k === "email"} type={k === "email" ? "email" : "text"} data-testid={`enquiry-${k}`}
                  placeholder={k[0].toUpperCase() + k.slice(1) + (k === "name" || k === "email" ? " *" : "")}
                  value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                  className="w-full rounded-xl border border-[#2A2E39] bg-[#0B0C0E] px-4 py-3 text-sm text-slate-100 placeholder:text-[#64748B] focus:border-[#D4AF37]/60 focus:outline-none" />
              ))}
              <textarea rows={3} placeholder="Message" data-testid="enquiry-message" value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                className="w-full rounded-xl border border-[#2A2E39] bg-[#0B0C0E] px-4 py-3 text-sm text-slate-100 placeholder:text-[#64748B] focus:border-[#D4AF37]/60 focus:outline-none" />
              <button data-testid="enquiry-submit-btn" className="mc-btn-gold inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3"><Send className="h-4 w-4" /> Send Enquiry</button>
            </form>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
